import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertCircle, CalendarDays, Check, MapPin, Users } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

/**
 * Public registration. No login, no sidebar, no app chrome.
 *
 * Most people reach this from a QR code on a phone, once, and will not come
 * back. So: one column, large targets, every field visible without hunting, and
 * a confirmation that can be screenshotted.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const { shareToken } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [ticket, setTicket] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", party_size: 1,
    dietary: [], dietary_notes: "", days: [], notify_channels: [],
  });

  useEffect(() => {
    axios.get(`${API}/public/event/${shareToken}/registration`)
      .then((res) => setInfo(res.data))
      .catch(() => setError("This link is not valid, or the event has been removed."))
      .finally(() => setLoading(false));
  }, [shareToken]);

  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value)
        ? f[key].filter((v) => v !== value)
        : [...f[key], value],
    }));

  const submit = async () => {
    setError("");
    if (!form.name.trim()) return setError("Please enter your name.");
    if (!form.email.includes("@")) return setError("Please enter a valid email address.");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/event/${shareToken}/register`, {
        ...form,
        // One person per registration unless the organiser opted in to parties.
        party_size: info?.allow_party ? (Number(form.party_size) || 1) : 1,
      });
      setTicket(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto space-y-3 pt-10">
          <div className="h-24 bg-muted rounded-xl animate-pulse" />
          <div className="h-64 bg-muted rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (error && !info) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm" data-testid="register-invalid">
          <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-foreground font-medium">{error}</p>
        </div>
      </div>
    );
  }

  // Confirmation. Deliberately screenshot-friendly — that is how people keep it.
  if (ticket) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto pt-10 space-y-4 text-center" data-testid="register-success">
          <div className="h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
            <Check className="h-7 w-7 text-emerald-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              {ticket.already_registered ? "Registration updated" : "You're registered"}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{info.event_title}</p>
          </div>
          <Card>
            <CardContent className="p-6 space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Your ticket code
              </p>
              <p className="text-3xl font-mono font-bold tracking-[0.2em] text-foreground"
                 data-testid="ticket-code">
                {ticket.code}
              </p>
              <p className="text-sm text-muted-foreground pt-2">
                {ticket.name}
                {ticket.party_size > 1 ? ` · ${ticket.party_size} people` : ""}
              </p>
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">
            Screenshot this or keep the email. Show the code at the door.
          </p>
          <a href={`/ticket/${ticket.code}`} className="inline-block">
            <Button variant="outline" className="h-11">View full ticket</Button>
          </a>
        </div>
      </div>
    );
  }

  const closed = !info.registration_open;
  const full = info.places_remaining === 0;

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto pt-8 pb-16 space-y-5">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-foreground">{info.event_title}</h1>
          <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-3">
            {info.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />{info.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              {info.start_date}{info.end_date !== info.start_date ? ` – ${info.end_date}` : ""}
            </span>
          </p>
          {info.registration_message && (
            <p className="text-sm text-muted-foreground pt-2">{info.registration_message}</p>
          )}
        </div>

        {closed || full ? (
          <Card data-testid="register-closed">
            <CardContent className="p-6 text-center space-y-2">
              <p className="font-medium text-foreground">
                {full ? "This event is full" : "Registration is closed"}
              </p>
              <p className="text-sm text-muted-foreground">
                Please contact the organisers if you think this is a mistake.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-5 space-y-4">
              {info.places_remaining !== null && info.places_remaining <= 20 && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Only {info.places_remaining} place{info.places_remaining === 1 ? "" : "s"} left
                </p>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="name">Your name</Label>
                <Input id="name" className="h-11" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       data-testid="register-name" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" className="h-11" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="register-email" />
                <p className="text-xs text-muted-foreground">
                  Registering again with this address updates your details.
                </p>
              </div>

              {/* One registration is one person unless the organiser turned
                  on parties: a ticket then maps to exactly one name at the
                  door and dietary needs are never averaged across a group. */}
              <div className={info.allow_party ? "grid grid-cols-2 gap-3" : ""}>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input id="phone" className="h-11" value={form.phone}
                         onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         data-testid="register-phone" />
                </div>
                {info.allow_party && (
                  <div className="space-y-1.5">
                    <Label htmlFor="party">How many people</Label>
                    <Input id="party" type="number" min="1" className="h-11"
                           value={form.party_size}
                           onChange={(e) => setForm({ ...form, party_size: e.target.value })}
                           data-testid="register-party" />
                  </div>
                )}
              </div>

              {info.days?.length > 1 && (
                <div className="space-y-1.5">
                  <Label>Which days? (leave blank for all)</Label>
                  <div className="flex flex-wrap gap-2">
                    {info.days.map((d) => (
                      <Button key={d.day_number} type="button" size="sm"
                              variant={form.days.includes(d.day_number) ? "default" : "outline"}
                              className="h-11"
                              onClick={() => toggle("days", d.day_number)}
                              data-testid={`register-day-${d.day_number}`}>
                        Day {d.day_number}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label>Dietary requirements</Label>
                <div className="flex flex-wrap gap-2">
                  {info.dietary_options.map((tag) => (
                    <Button key={tag} type="button" size="sm"
                            variant={form.dietary.includes(tag) ? "default" : "outline"}
                            className="h-11"
                            onClick={() => toggle("dietary", tag)}
                            data-testid={`register-diet-${tag}`}>
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Only offered once a number is present — the backend drops
                  text-message opt-ins with nothing to send to. */}
              {form.phone.trim() && (
                <div className="space-y-1.5">
                  <Label>How should we reach you?</Label>
                  <div className="flex flex-wrap gap-2">
                    {[["email", "Email"], ["sms", "Text message"], ["whatsapp", "WhatsApp"]]
                      .map(([value, label]) => (
                        <Button key={value} type="button" size="sm"
                                variant={form.notify_channels.includes(value) ? "default" : "outline"}
                                className="h-11"
                                onClick={() => toggle("notify_channels", value)}
                                data-testid={`register-channel-${value}`}>
                          {label}
                        </Button>
                      ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    We'll email your ticket either way.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="notes">Allergies or anything else we should know</Label>
                <Textarea id="notes" rows={2} value={form.dietary_notes}
                          onChange={(e) => setForm({ ...form, dietary_notes: e.target.value })}
                          data-testid="register-notes" />
              </div>

              {error && (
                <p className="text-sm text-destructive flex items-start gap-1.5"
                   data-testid="register-error">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />{error}
                </p>
              )}

              {/* Allergy information is health-related. Saying what happens to
                  it belongs next to the field that collects it, not in a policy
                  nobody opens. */}
              <p className="text-xs text-muted-foreground leading-relaxed"
                 data-testid="register-privacy">
                Your details are used only to plan this event. Dietary and allergy notes
                are shared with the organisers and caterers so they can feed you safely.
                You can withdraw your registration any time from your ticket.
              </p>

              <Button className="w-full h-12 text-base" onClick={submit} disabled={submitting}
                      data-testid="register-submit">
                {submitting ? "Registering…" : "Register"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
